import { Capacitor } from '@capacitor/core';
import { loadDeviceIdentity } from '../auth/auth';
import { getMeta } from '../data/appMeta';
import type { SqlDriver } from '../data/db/driver';
import { schemaVersion } from '../data/db/migrations';
import { createStore } from '../data/store/store';
import { HlcClock } from '../domain/hlc';
import { createI18n, detectLanguage, isLanguage } from '../i18n/i18n';
import { cloudConfig, createSupabaseCloud, type Cloud } from '../sync/cloud';
import { fixedEntitlement, type EntitlementSource, type Storefront } from '../premium/entitlement';
import { revenueCat, revenueCatKey } from '../premium/revenuecat';
import { accountEntitlement, eitherEntitlement } from '../premium/account';
import { admobBridge, bannerUnit, type AdsBridge } from '../ads/ads';
import { ACTIVE_SPACE_KEY, SAMPLE_DATA_KEY, LANGUAGE_KEY, THEME_KEY, applyTheme, isThemePreference, type AppServices } from './services';

/** Placeholder until onboarding asks for a name. */
const LOCAL_USER_NAME = 'Me';

/** Startup: open + migrate the local DB, load identity, language and theme. No network. */
export async function bootstrap(
  openDb: () => Promise<SqlDriver>,
  options: { cloud?: (db: SqlDriver) => Cloud | null; premium?: EntitlementSource; storefront?: Storefront | null; ads?: AdsBridge | null } = {},
): Promise<AppServices> {
  const db = await openDb();
  const [identity, storedLanguage, storedTheme, version] = await Promise.all([
    loadDeviceIdentity(db),
    getMeta(db, LANGUAGE_KEY),
    getMeta(db, THEME_KEY),
    schemaVersion(db),
  ]);
  // HLC node = this install, so concurrent edits from two devices order deterministically.
  const store = createStore({ db, clock: new HlcClock(identity.deviceId), now: Date.now });
  await store.users.ensureLocal(identity.session.userId, LOCAL_USER_NAME);

  // The budget on screen: the remembered one if it still exists, else the user's first.
  const spaces = await store.spaces.listForUser(identity.session.userId);
  const rememberedId = await getMeta(db, ACTIVE_SPACE_KEY);
  const activeSpace = spaces.find((s) => s.id === rememberedId) ?? spaces[0] ?? null;
  const sampleData = (await getMeta(db, SAMPLE_DATA_KEY)) === '1';

  const language = isLanguage(storedLanguage) ? storedLanguage : detectLanguage();
  const theme = isThemePreference(storedTheme) ? storedTheme : 'system';
  const i18n = await createI18n(language);
  const platform = Capacitor.getPlatform() as AppServices['platform'];
  const shop = platform === 'web' ? null : revenueCat(revenueCatKey(platform));
  if (typeof document !== 'undefined') document.documentElement.lang = language;
  applyTheme(theme);
  // No network at startup: the client is created here but only talks when asked to.
  const cloud = options.cloud
    ? options.cloud(db)
    : (() => {
        const config = cloudConfig();
        return config ? createSupabaseCloud(db, config) : null;
      })();
  // Phones: the store (RevenueCat), else the account (bought on another device). The web: the account.
  const account = accountEntitlement(cloud, db);
  return {
    db,
    store,
    identity,
    i18n,
    platform,
    schemaVersion: version,
    theme,
    activeSpace,
    sampleData,
    cloud,
    premium: options.premium ?? (shop ? eitherEntitlement(shop.entitlement, account) : webEntitlement(account)),
    storefront: options.storefront !== undefined ? options.storefront : (shop?.storefront ?? null),
    ads:
      options.ads !== undefined
        ? options.ads
        : platform === 'web'
          ? null
          : (() => {
              const unit = bannerUnit(platform);
              return unit ? admobBridge(unit) : null;
            })(),
    restart: () => window.location.reload(),
  };
}

/**
 * The web: Premium comes with the account. End-to-end test builds (vite
 * build --mode e2e) are Premium unless a test turns it off (localStorage
 * e2e.premium = 0); that branch is not in a production build.
 */
function webEntitlement(account: EntitlementSource): EntitlementSource {
  if (import.meta.env.VITE_E2E === '1') return fixedEntitlement(localStorage.getItem('e2e.premium') !== '0');
  return account;
}
